// Cards-module adapter over the shared document-type matrix (`@/shared/data/document-types`). The matrix
// itself is shared because the clients module has the same per-country problem; only the i18n label key is
// cards-specific, so that is all this module adds.

import {
  type DocumentTypeCode,
  type FixedLengthDocumentCode,
  SUPPORTED_IDENTITY_COUNTRIES,
} from "@/shared/data/document-types";

export { documentTypesFor, isDocumentNumberLengthValid } from "@/shared/data/document-types";
export type {
  DocumentTypeCategory,
  DocumentTypeCode,
  IdentityCountryCode,
} from "@/shared/data/document-types";

/** ISO 3166-1 alpha-3 codes with a country-specific matrix, aliased for the identities flow. */
export const IDENTITY_SUPPORTED_COUNTRIES = SUPPORTED_IDENTITY_COUNTRIES;

/** I18n key (cards namespace) for a document-type code — e.g. "CC" -> "create_identity_id_type_cc". */
export const documentTypeLabelKey = <C extends DocumentTypeCode>(code: C) =>
  `create_identity_id_type_${code.toLowerCase() as Lowercase<C>}` as const;

export type DocumentLengthMessageKey =
  | "create_identity_error_cpf_digits"
  | "create_identity_error_cnpj_length";

/**
 * Error message key (cards namespace) for each document type that has a fixed length. The clients module
 * keeps its own copy of this map, since the two namespaces spell the key differently; what they must not do
 * is disagree on WHICH codes have a rule — hence both are keyed by `FixedLengthDocumentCode`, so adding a
 * rule without a message fails to compile on both sides.
 */
export const DOCUMENT_LENGTH_MESSAGE_KEYS: Record<FixedLengthDocumentCode, DocumentLengthMessageKey> = {
  CPF: "create_identity_error_cpf_digits",
  CNPJ: "create_identity_error_cnpj_length",
};

export const documentLengthMessageKey = (code: string): DocumentLengthMessageKey | undefined =>
  DOCUMENT_LENGTH_MESSAGE_KEYS[code as FixedLengthDocumentCode];
